import { useMemo, useState } from 'react';
import type { EntryStopMatrixCell, EntryStopMatrixView } from '../../analytics/entry-stop-matrix.ts';
import { findMatrixCell } from '../../analytics/entry-stop-matrix.ts';
import {
  DEFAULT_FIXED_STAKE,
  defaultEntryStopRange,
  isValidFixedMatrixCell,
  MatrixRangeError,
  requestedMatrixCellCount,
  signedHeatWeight,
} from '../../analytics/fixed-grid.ts';
import { formatProfitExposureRatio, terminalEvidenceLabel } from '../../analytics/martingale-grid.ts';
import { MAX_PROFIT_MATRIX_CELLS, PROFIT_MATRIX_QUICK_TARGETS } from '../../shared/constants.ts';
import {
  formatDecimal,
  formatInteger,
  formatPercent,
  formatSignedUnits,
  formatTarget,
  formatUnits,
} from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { StatusMessage } from './StatusMessage.tsx';

type QuickTarget = (typeof PROFIT_MATRIX_QUICK_TARGETS)[number] | 'custom';
type HeatmapTab = 'net-profit' | 'profit-exposure';

function rangeFromTarget(target: number) {
  const range = defaultEntryStopRange(target);
  return {
    entryFrom: String(range.entryFrom),
    entryTo: String(range.entryTo),
    stopFrom: String(range.stopFrom),
    stopTo: String(range.stopTo),
  };
}

function compactPnl(value: number): string {
  const abs = formatDecimal(Math.abs(value), 1);
  if (value > 0) {
    return `+${abs}`;
  }
  if (value < 0) {
    return `−${abs}`;
  }
  return abs;
}

function pnlTone(value: number): string {
  if (value > 0) {
    return 'text-positive';
  }
  if (value < 0) {
    return 'text-negative';
  }
  return 'text-ink';
}

function heatmapBackground(value: number, maxPositive: number, maxNegative: number): string {
  const weight = signedHeatWeight(value, maxPositive, maxNegative);
  if (weight < 0.03) {
    return 'rgba(236, 234, 228, 0.85)';
  }
  const alpha = 0.12 + 0.5 * weight;
  if (value > 0) {
    return `rgba(47, 93, 74, ${alpha})`;
  }
  return `rgba(143, 61, 61, ${alpha})`;
}

function cellHeatValue(cell: EntryStopMatrixCell, tab: HeatmapTab): number {
  if (tab === 'profit-exposure') {
    return cell.profitExposureRatio ?? 0;
  }
  return cell.netPnl;
}

function cellHeatLabel(cell: EntryStopMatrixCell, tab: HeatmapTab): string {
  if (tab === 'profit-exposure') {
    return formatProfitExposureRatio(cell.profitExposureRatio);
  }
  return compactPnl(cell.netPnl);
}

function evidenceBorderClass(band: EntryStopMatrixCell['terminalEvidenceBand']): string {
  if (band === 'UNT') {
    return 'border-dashed border-warn/70';
  }
  if (band === 'lo') {
    return 'border-solid border-warn/40';
  }
  if (band === 'md') {
    return 'border-solid border-line';
  }
  return 'border-solid border-ink/40';
}

export function EntryStopMatrixSection() {
  const [quickTarget, setQuickTarget] = useState<QuickTarget>(2);
  const [customTarget, setCustomTarget] = useState('2');
  const [entryFrom, setEntryFrom] = useState('2');
  const [entryTo, setEntryTo] = useState('10');
  const [stopFrom, setStopFrom] = useState('4');
  const [stopTo, setStopTo] = useState('21');
  const [baseStake, setBaseStake] = useState(String(DEFAULT_FIXED_STAKE));
  const [stakingMode, setStakingMode] = useState<'FIXED' | 'MARTINGALE'>('FIXED');
  const [view, setView] = useState<EntryStopMatrixView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<{ entryDrought: number; stopDrought: number } | null>(null);
  const [heatmapTab, setHeatmapTab] = useState<HeatmapTab>('net-profit');

  const targetMultiplier = quickTarget === 'custom' ? Number(customTarget) : quickTarget;
  const heatmapMode: 'FIXED' | 'MARTINGALE' = targetMultiplier === 2 ? stakingMode : 'FIXED';

  const selectedCell = useMemo(() => {
    if (!view || !selected) {
      return null;
    }
    return findMatrixCell(view.cells, selected.entryDrought, selected.stopDrought);
  }, [view, selected]);

  function applyTarget(next: QuickTarget, customValue?: string) {
    setQuickTarget(next);
    const numeric = next === 'custom' ? Number(customValue ?? customTarget) : next;
    if (Number.isFinite(numeric) && numeric >= 1) {
      const nextRange = rangeFromTarget(numeric);
      setEntryFrom(nextRange.entryFrom);
      setEntryTo(nextRange.entryTo);
      setStopFrom(nextRange.stopFrom);
      setStopTo(nextRange.stopTo);
      setView(null);
      setSelected(null);
      if (numeric !== 2) {
        setStakingMode('FIXED');
      }
    }
  }

  function applyTargetDefaults() {
    if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
      setError('Target multiplier must be a finite number ≥ 1.');
      return;
    }
    const nextRange = rangeFromTarget(targetMultiplier);
    setEntryFrom(nextRange.entryFrom);
    setEntryTo(nextRange.entryTo);
    setStopFrom(nextRange.stopFrom);
    setStopTo(nextRange.stopTo);
  }

  async function loadMatrix() {
    const from = Number(entryFrom);
    const to = Number(entryTo);
    const sFrom = Number(stopFrom);
    const sTo = Number(stopTo);
    const stake = Number(baseStake);
    if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
      setError('Target multiplier must be a finite number ≥ 1.');
      return;
    }
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      !Number.isInteger(sFrom) ||
      !Number.isInteger(sTo) ||
      from < 1 ||
      sFrom < 1 ||
      to < from ||
      sTo < sFrom
    ) {
      setError('Entry D and Stop S ranges must be integers with from ≤ to.');
      return;
    }
    if (!Number.isFinite(stake) || stake <= 0) {
      setError('Fixed stake must be a finite number > 0.');
      return;
    }
    const requested = requestedMatrixCellCount({
      entryFrom: from,
      entryTo: to,
      stopFrom: sFrom,
      stopTo: sTo,
    });
    if (requested > MAX_PROFIT_MATRIX_CELLS) {
      setError(new MatrixRangeError(requested).message);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<EntryStopMatrixView>('/api/analytics/profit-matrix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetMultiplier,
          stakingMode: heatmapMode,
          baseStake: stake,
          entryFrom: from,
          entryTo: to,
          stopFrom: sFrom,
          stopTo: sTo,
        }),
      });
      setView(body);
      const keep =
        selected && findMatrixCell(body.cells, selected.entryDrought, selected.stopDrought)
          ? selected
          : body.cells[0]
            ? { entryDrought: body.cells[0].entryDrought, stopDrought: body.cells[0].stopDrought }
            : null;
      setSelected(keep);
    } catch (cause) {
      setView(null);
      setError(cause instanceof Error ? cause.message : 'Matrix calculation failed');
    } finally {
      setBusy(false);
    }
  }

  const requestedPreview = (() => {
    const from = Number(entryFrom);
    const to = Number(entryTo);
    const sFrom = Number(stopFrom);
    const sTo = Number(stopTo);
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      !Number.isInteger(sFrom) ||
      !Number.isInteger(sTo) ||
      to < from ||
      sTo < sFrom
    ) {
      return null;
    }
    return requestedMatrixCellCount({ entryFrom: from, entryTo: to, stopFrom: sFrom, stopTo: sTo });
  })();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Historical Entry / Stop matrix. Net Profit and Profit / Exposure are two views of the same results. Fixed
        staking works for any target. Target 2x also offers the restored 2x Martingale matrix. Cells are in-sample
        simulation results, not forecasts.
      </p>
      <form
        className="space-y-4 rounded border border-line bg-panel p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void loadMatrix();
        }}
      >
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted">Target multiplier</legend>
          <div className="mt-2 flex flex-wrap gap-1" role="group" aria-label="Target multiplier">
            {PROFIT_MATRIX_QUICK_TARGETS.map((target) => (
              <button
                key={target}
                type="button"
                className={`rounded border px-3 py-1 text-sm ${
                  quickTarget === target ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
                }`}
                aria-pressed={quickTarget === target}
                onClick={() => applyTarget(target)}
              >
                {target}x
              </button>
            ))}
            <button
              type="button"
              className={`rounded border px-3 py-1 text-sm ${
                quickTarget === 'custom' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
              }`}
              aria-pressed={quickTarget === 'custom'}
              onClick={() => {
                const current = Number.isFinite(targetMultiplier) ? String(targetMultiplier) : customTarget;
                setCustomTarget(current);
                applyTarget('custom', current);
              }}
            >
              Custom
            </button>
          </div>
          {quickTarget === 'custom' ? (
            <label className="mt-2 flex max-w-xs flex-col gap-1 text-sm">
              Custom target
              <input
                className="rounded border border-line bg-page px-2 py-2"
                inputMode="decimal"
                value={customTarget}
                aria-label="Custom target multiplier"
                onChange={(event) => {
                  setCustomTarget(event.target.value);
                  setView(null);
                  const numeric = Number(event.target.value);
                  if (Number.isFinite(numeric) && numeric !== 2) {
                    setStakingMode('FIXED');
                  }
                }}
              />
            </label>
          ) : null}
          <p className="mt-2 text-xs text-muted">HIT = multiplier ≥ selected target. MISS = multiplier &lt; selected target.</p>
        </fieldset>
        <fieldset>
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted">Staking mode</legend>
          <div className="mt-2 flex flex-wrap gap-1" role="group" aria-label="Staking mode">
            <button
              type="button"
              className={`rounded border px-3 py-1 text-sm ${
                heatmapMode === 'FIXED' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
              }`}
              aria-pressed={heatmapMode === 'FIXED'}
              onClick={() => {
                setStakingMode('FIXED');
                setView(null);
              }}
            >
              Fixed
            </button>
            {targetMultiplier === 2 ? (
              <button
                type="button"
                className={`rounded border px-3 py-1 text-sm ${
                  heatmapMode === 'MARTINGALE' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
                }`}
                aria-pressed={heatmapMode === 'MARTINGALE'}
                onClick={() => {
                  setStakingMode('MARTINGALE');
                  setView(null);
                }}
              >
                Martingale
              </button>
            ) : null}
          </div>
          {targetMultiplier === 2 ? (
            <p className="mt-2 text-xs text-muted">
              Martingale uses the historical 2x doubling engine (stakes 1, 2, 4, …). Fixed uses the same stake on every
              wager.
            </p>
          ) : (
            <p className="mt-2 text-xs text-muted">Martingale is available only for target 2x. This matrix uses Fixed.</p>
          )}
        </fieldset>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          <label className="flex flex-col gap-1 text-sm">
            Minimum Entry D
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="numeric"
              value={entryFrom}
              onChange={(event) => setEntryFrom(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Maximum Entry D
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="numeric"
              value={entryTo}
              onChange={(event) => setEntryTo(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Minimum Stop S
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="numeric"
              value={stopFrom}
              onChange={(event) => setStopFrom(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Maximum Stop S
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="numeric"
              value={stopTo}
              onChange={(event) => setStopTo(event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            {heatmapMode === 'MARTINGALE' ? 'Base stake' : 'Fixed stake'}
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="decimal"
              aria-label={heatmapMode === 'MARTINGALE' ? 'Base stake' : 'Fixed stake'}
              value={baseStake}
              onChange={(event) => setBaseStake(event.target.value)}
            />
          </label>
          <div className="flex flex-col justify-end gap-2">
            <button type="button" className="rounded border border-line bg-page px-3 py-2 text-sm" onClick={applyTargetDefaults}>
              Use target defaults
            </button>
            <button type="submit" className="rounded bg-accent px-4 py-2 text-sm text-panel disabled:opacity-60" disabled={busy}>
              {busy ? 'Calculating…' : view ? 'Recalculate matrix' : 'Calculate matrix'}
            </button>
          </div>
        </div>
        {requestedPreview !== null ? (
          <p className={`text-xs ${requestedPreview > MAX_PROFIT_MATRIX_CELLS ? 'text-negative' : 'text-muted'}`}>
            Requested cells: {requestedPreview.toLocaleString('en-US')} (limit {MAX_PROFIT_MATRIX_CELLS.toLocaleString('en-US')}).
            Invalid D/S combinations stay blank and still count toward this limit.
          </p>
        ) : null}
      </form>

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {busy && !view ? <StatusMessage kind="loading">Calculating entry/stop matrix</StatusMessage> : null}

      {view ? (
        <>
          <p className="text-xs text-muted">{view.note}</p>
          <PaceSummary view={view} />
          <section className="rounded border border-line bg-panel p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Entry / Stop Matrix</h2>
            <p className="mt-1 text-sm">
              Target {formatTarget(view.targetMultiplier)} · {view.stakingMode} stake {formatUnits(view.baseStake)} ·{' '}
              {formatInteger(view.validCells)} valid cells of {formatInteger(view.requestedCells)} requested
            </p>
            <div className="mt-3 flex flex-wrap gap-1" role="tablist" aria-label="Entry / Stop matrix views">
              <button
                type="button"
                role="tab"
                aria-selected={heatmapTab === 'net-profit'}
                className={`rounded border px-3 py-1 text-sm ${
                  heatmapTab === 'net-profit' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
                }`}
                onClick={() => setHeatmapTab('net-profit')}
              >
                Net Profit
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={heatmapTab === 'profit-exposure'}
                className={`rounded border px-3 py-1 text-sm ${
                  heatmapTab === 'profit-exposure' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
                }`}
                onClick={() => setHeatmapTab('profit-exposure')}
              >
                Profit / Exposure
              </button>
            </div>
            <p className="mt-2 text-xs text-muted">{view.invalidCellRule}</p>
            <MatrixGrid
              view={view}
              tab={heatmapTab}
              selected={selected}
              onSelect={(entryDrought, stopDrought) => setSelected({ entryDrought, stopDrought })}
            />
            <HeatmapLegend tab={heatmapTab} />
          </section>
          {selectedCell ? <CellDetail cell={selectedCell} view={view} /> : null}
        </>
      ) : null}
    </div>
  );
}

function PaceSummary(props: { view: EntryStopMatrixView }) {
  const pace = props.view.pace;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Sample and continuity</h2>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted">Source rounds</dt>
          <dd>{formatInteger(props.view.rounds)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Continuity segments</dt>
          <dd>{formatInteger(props.view.continuitySegments)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Longest completed drought</dt>
          <dd>{formatInteger(props.view.observedLongestCompletedDrought)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Combined rounds/hour</dt>
          <dd>{formatDecimal(pace.combinedRoundsPerHour, 1)}</dd>
        </div>
      </dl>
      {props.view.continuitySegments > 1 ? (
        <p className="mt-2 text-xs text-muted">
          This sample has {formatInteger(props.view.continuitySegments)} continuity segments. Strategies and droughts do
          not cross proven gaps; an open betting episode at a gap is censored.
        </p>
      ) : null}
    </section>
  );
}

function MatrixGrid(props: {
  view: EntryStopMatrixView;
  tab: HeatmapTab;
  selected: { entryDrought: number; stopDrought: number } | null;
  onSelect: (entryDrought: number, stopDrought: number) => void;
}) {
  const { view, tab } = props;
  const values = view.cells.map((cell) => cellHeatValue(cell, tab));
  const maxPositive = Math.max(0, ...values.map((value) => Math.max(0, value)));
  const maxNegative = Math.max(0, ...values.map((value) => Math.max(0, -value)));
  return (
    <div className="table-scroll mt-3">
      <table className="min-w-[720px] w-full border-collapse text-center text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 bg-panel px-2 py-2 text-left font-medium text-muted">ENTRY \\ STOP S</th>
            {view.stopColumns.map((stop) => (
              <th key={stop} className="px-1 py-2 font-medium text-muted">
                {stop}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.entryRows.map((entry) => (
            <tr key={entry}>
              <th className="sticky left-0 bg-panel px-2 py-1 text-left font-medium">D{entry}</th>
              {view.stopColumns.map((stop) => {
                const valid = isValidFixedMatrixCell(entry, stop);
                const cell = findMatrixCell(view.cells, entry, stop);
                if (!valid || !cell) {
                  return <td key={stop} className="px-1 py-1" aria-label={`D${entry} stop S${stop} invalid`} />;
                }
                const selected = props.selected?.entryDrought === entry && props.selected.stopDrought === stop;
                const evidence = cell.terminalEvidenceBand;
                const heatValue = cellHeatValue(cell, tab);
                const label = cellHeatLabel(cell, tab);
                const metricName = tab === 'profit-exposure' ? 'profit / exposure' : 'net P&L';
                return (
                  <td key={stop} className="p-0">
                    <button
                      type="button"
                      className={`w-full rounded border px-1 py-2 ${evidenceBorderClass(evidence)} ${
                        selected ? 'ring-2 ring-accent' : ''
                      }`}
                      style={{ backgroundColor: heatmapBackground(heatValue, maxPositive, maxNegative) }}
                      title={
                        tab === 'profit-exposure'
                          ? `Profit / exposure ${formatProfitExposureRatio(cell.profitExposureRatio)}\nHistorical net P&L ${formatSignedUnits(cell.netPnl, 1)}\n${terminalEvidenceLabel(evidence)} (${formatInteger(cell.stopOuts)} stop-outs)`
                          : `Historical net P&L ${formatSignedUnits(cell.netPnl, 1)}\n${terminalEvidenceLabel(evidence)} (${formatInteger(cell.stopOuts)} stop-outs)`
                      }
                      aria-pressed={selected}
                      aria-label={`D${entry} stop S${stop} ${metricName} ${label} ${evidence} ${terminalEvidenceLabel(evidence)}`}
                      onClick={() => props.onSelect(entry, stop)}
                    >
                      <span className={`block text-sm font-semibold ${pnlTone(heatValue)}`}>{label}</span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeatmapLegend(props: { tab: HeatmapTab }) {
  const negative = props.tab === 'profit-exposure' ? 'Negative profit / exposure' : 'Negative historical P&L';
  const positive = props.tab === 'profit-exposure' ? 'Positive profit / exposure' : 'Positive historical P&L';
  const heatNote =
    props.tab === 'profit-exposure'
      ? 'Heat intensity is the signed historical net P&L / maximum theoretical episode exposure. Negative ratios stay negative; they are not N/A.'
      : 'Heat intensity is historical net P&L only. It is not a combined score and does not represent predicted profitability.';
  return (
    <div className="mt-3 space-y-2 text-xs text-muted">
      <div className="flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: 'rgba(143, 61, 61, 0.45)' }} />
          {negative}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded border border-line"
            style={{ backgroundColor: 'rgba(236, 234, 228, 0.85)' }}
          />
          Near zero
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: 'rgba(47, 93, 74, 0.62)' }} />
          {positive}
        </span>
      </div>
      <p>{heatNote}</p>
      <div className="flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded border border-dashed border-warn/70" />
          Unobserved terminal risk (0 stop-outs)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded border border-solid border-warn/40" />
          Low evidence (1–4)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded border border-solid border-line" />
          Moderate (5–9)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded border border-solid border-ink/40" />
          Stronger (10+)
        </span>
      </div>
    </div>
  );
}

function CellDetail(props: { cell: EntryStopMatrixCell; view: EntryStopMatrixView }) {
  const { cell, view } = props;
  const evidence = cell.terminalEvidenceBand;
  return (
    <section className="rounded border border-line bg-panel p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
        D{cell.entryDrought} → S{cell.stopDrought}
      </h2>
      <p className="mt-1 text-sm">
        Target {formatTarget(view.targetMultiplier)} · staking {view.stakingMode === 'MARTINGALE' ? 'Martingale' : 'FIXED'} ·
        Entry D{cell.entryDrought} · Stop S{cell.stopDrought} · {view.stakingMode === 'MARTINGALE' ? 'base' : 'fixed'} stake{' '}
        {formatUnits(view.baseStake)}
      </p>
      {view.stakingMode === 'MARTINGALE' ? (
        <p className="mt-1 text-sm">
          Stake progression {cell.stakeProgression.join(' → ')}. Number of possible wagers {formatInteger(cell.wagerCount)}.
          Theoretical maximum exposure {formatUnits(cell.theoreticalFullStopExposure, 0)}.
        </p>
      ) : (
        <p className="mt-1 text-sm">
          Number of possible wagers {formatInteger(cell.wagerCount)}. Theoretical maximum exposure{' '}
          {formatUnits(cell.theoreticalFullStopExposure, 0)}.
        </p>
      )}
      <p className="mt-2 text-xs text-muted">{view.hitRule}</p>
      <p className="mt-1 text-xs text-muted">{view.settlementRule}</p>
      <p className="mt-1 text-xs text-muted">{view.reentryRule}</p>
      {cell.terminalLossNotObservedNote ? (
        <p className="mt-2 rounded border border-warn/50 px-3 py-2 text-sm text-warn" role="status">
          {cell.terminalLossNotObservedNote}. Terminal evidence {evidence}: {formatInteger(cell.stopOuts)} observed
          terminal stop-outs of {formatInteger(cell.triggeredEpisodes)} triggered episodes.
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted">
          Terminal stop depth observed: YES. Terminal evidence {evidence}: {formatInteger(cell.stopOuts)} stop-outs (
          {terminalEvidenceLabel(evidence)}).
        </p>
      )}
      <p className="mt-2 text-xs text-muted">{cell.sampleSizeNote} This does not imply future profitability.</p>
      {cell.stopExceedsObservedLongest ? (
        <p className="mt-2 text-xs text-muted">
          Stop S exceeds the observed longest completed drought ({formatInteger(cell.observedLongestDrought)}). No
          fabricated terminal events.
        </p>
      ) : null}
      {view.continuitySegments > 1 || cell.censoredEpisodes > 0 ? (
        <p className="mt-2 text-xs text-muted">
          Continuity: {formatInteger(view.continuitySegments)} segment
          {view.continuitySegments === 1 ? '' : 's'}. Censored episodes {formatInteger(cell.censoredEpisodes)}. Gaps
          censor open betting episodes and are not crossed.
        </p>
      ) : null}
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-4">
        <Metric label="Triggered episodes" value={formatInteger(cell.triggeredEpisodes)} />
        <Metric label="Completed episodes" value={formatInteger(cell.completedEpisodes)} />
        <Metric label="Successful episodes" value={formatInteger(cell.successfulEpisodes)} />
        <Metric label="Stop-outs" value={formatInteger(cell.stopOuts)} />
        <Metric label="Open episodes" value={formatInteger(cell.openEpisodes)} />
        <Metric label="Censored episodes" value={formatInteger(cell.censoredEpisodes)} />
        <Metric label="Total wagers" value={formatInteger(cell.totalWagers)} />
        <Metric label="Total amount staked" value={formatUnits(cell.totalStaked)} />
        <Metric label="Gross return" value={formatUnits(cell.grossReturned)} />
        <Metric label="Net P&L" value={formatSignedUnits(cell.netPnl)} />
        <Metric label="ROI" value={formatPercent(cell.roi, 1)} />
        <Metric label="Maximum theoretical episode exposure" value={formatUnits(cell.theoreticalFullStopExposure)} />
        <Metric label="Actual maximum wager" value={formatUnits(cell.largestActualWager)} />
        <Metric label="Actual maximum observed episode exposure" value={formatUnits(cell.largestActualEpisodeExposure)} />
        <Metric label="Profit / Exposure" value={formatProfitExposureRatio(cell.profitExposureRatio)} />
        {view.stakingMode === 'MARTINGALE' ? (
          <>
            <Metric label="P&L after one hypothetical additional full stop" value={formatSignedUnits(cell.pnlAfterOneExtraFullStop)} />
            <Metric label="Extra full stops survivable" value={formatInteger(cell.extraFullStopsSurvivable)} />
            <Metric label="Maximum drawdown" value={formatUnits(cell.maximumDrawdown)} />
          </>
        ) : null}
        <Metric label="Net P&L per 1,000 source rounds" value={formatDecimal(cell.unitsPer1000Rounds, 2)} />
        <Metric label="Success rate" value={formatPercent(cell.successRate, 1)} />
        <Metric label="Stop-out rate" value={formatPercent(cell.stopOutRate, 2)} />
        <Metric label="Longest relevant observed drought" value={formatInteger(cell.longestTriggeredDrought)} />
        <Metric label="Sample longest completed drought" value={formatInteger(cell.observedLongestDrought)} />
        <Metric label="Terminal stop depth observed" value={cell.terminalStopOutObserved ? 'YES' : 'NO'} />
        <Metric label="Terminal evidence count" value={formatInteger(cell.stopOuts)} />
        <Metric label="Number of possible wagers" value={formatInteger(cell.wagerCount)} />
      </dl>
    </section>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-sm font-medium">{props.value}</dd>
    </div>
  );
}
